/**
 * Entitlements Store API
 *
 * Re-exports subscriptions API utilities and provides entitlements-specific hooks.
 * This module decouples entitlements from UserInfo cache, enabling targeted invalidation.
 */

import { useMemo } from 'react';
import { subscriptionsApi, useGetEntitlementsQuery } from '@features/subscriptions';

import type { EntitlementResponse } from '../types';

// TEMPORARY: Set to true to force preview entitlement state
const __DEV_FORCE_PREVIEW__ = false;

const MOCK_ENTITLEMENT: EntitlementResponse = {
  assignment_source: 'stripe',
  bundle_id: 'mock-bundle-id',
  bundle_name: 'Low-Volume',
  capability_id: 'mock-capability-id',
  capability_name: 'template_creation',
  capability_description: 'Create custom note templates',
  quota: 2,
  current_usage: 2, // Set to quota for depleted state
  quota_remaining: 0,
  usage_period: 'total',
  metering_type: 'none',
  current_period_end: null,
};

/**
 * Re-export subscriptionsApi for cache invalidation utilities
 * Use this to invalidate Entitlements tag: subscriptionsApi.util.invalidateTags(['Entitlements'])
 */
export { subscriptionsApi };

/**
 * Creates an optimistic update for entitlement usage.
 * Returns a PatchCollection that can be reverted on error.
 *
 * @param capabilityName - The capability name to update (e.g., 'note_creation', 'form_sending')
 * @param incrementBy - Amount to increment usage by (default: 1)
 * @returns A patch result that can be undone with `.undo()` on error
 *
 * @example
 * ```typescript
 * const patchResult = dispatch(optimisticallyIncrementEntitlement('note_creation'));
 * try {
 *   await queryFulfilled;
 *   dispatch(subscriptionsApi.util.invalidateTags(['Entitlements']));
 * } catch {
 *   patchResult?.undo();
 * }
 * ```
 */
export const optimisticallyIncrementEntitlement = (capabilityName: string, incrementBy = 1) =>
  subscriptionsApi.util.updateQueryData('getV2ProviderEntitlements', undefined, draft => {
    const entitlement = draft?.entitlements?.find(
      e => e.capability_name?.toLowerCase() === capabilityName.toLowerCase(),
    );
    if (
      entitlement &&
      entitlement.current_usage !== undefined &&
      entitlement.current_usage !== null
    ) {
      entitlement.current_usage += incrementBy;
      if (entitlement.quota_remaining !== undefined && entitlement.quota_remaining !== null) {
        entitlement.quota_remaining = Math.max(0, entitlement.quota_remaining - incrementBy);
      }
    }
  });

/**
 * Hook to get a specific entitlement by capability name
 *
 * @param capabilityName - The capability name to find (e.g., 'note_creation', 'form_sending')
 * @param options - Optional RTK Query options
 * @returns The matching entitlement or undefined
 *
 * @example
 * ```tsx
 * const entitlement = useEntitlement('note_creation');
 * ```
 */
export function useEntitlement(
  capabilityName: string,
  options?: {
    skip?: boolean;
    refetchOnMountOrArgChange?: boolean;
    refetchOnFocus?: boolean;
  },
) {
  // Get entitlements query with entitlements-specific options
  const {
    data: entitlementsData,
    isLoading,
    isFetching,
  } = useGetEntitlementsQuery(undefined, {
    skip: options?.skip ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? true,
  });

  // Find entitlement matching the capability name (case-insensitive)
  const entitlement = useMemo<EntitlementResponse | undefined>(() => {
    if (!entitlementsData?.entitlements) return undefined;
    return entitlementsData.entitlements.find(
      (e: EntitlementResponse) => e.capability_name?.toLowerCase() === capabilityName.toLowerCase(),
    );
  }, [entitlementsData, capabilityName]);

  // Override with mock values when dev preview is enabled
  if (__DEV_FORCE_PREVIEW__) {
    return {
      entitlement: MOCK_ENTITLEMENT,
      isLoading: false,
      isFetching: false,
    };
  }

  return {
    entitlement,
    isLoading,
    isFetching,
  };
}

/**
 * Hook to get all entitlements
 *
 * @param options - Optional RTK Query options
 * @returns All entitlements array
 *
 * @example
 * ```tsx
 * const { entitlements, isLoading } = useEntitlements();
 * ```
 */
export function useEntitlements(options?: {
  skip?: boolean;
  refetchOnMountOrArgChange?: boolean;
  refetchOnFocus?: boolean;
}) {
  const {
    data: entitlementsData,
    isLoading,
    isFetching,
  } = useGetEntitlementsQuery(undefined, {
    skip: options?.skip ?? false,
    refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange ?? true,
    refetchOnFocus: options?.refetchOnFocus ?? true,
  });

  return {
    entitlements: entitlementsData?.entitlements || [],
    isLoading,
    isFetching,
  };
}
